// Kiosk ticket workspace — Phase 2 (S67d): tab architecture.
// Ticket always pre-exists (created eagerly at KioskShell level before this
// mounts). Bridge selection is lifted to KioskShell. Each tab carries its own
// WbTicketProvider and active-line state persisted in useKioskTabs.
//
//   LEFT  (~22%)  details header (⋯ overflow) + customer + vehicle + notes
//   MAIN  (~52%)  line column (left 30%) + weight panel (right 70%)
//   RIGHT (~26%)  totals + actions

import { createPortal } from 'react-dom';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useKioskTheme } from './hooks/useKioskTheme';
import { ScanLine } from 'lucide-react';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { deleteWbTicket } from '../../../api/wbTickets';
import { useAuthStore } from '../../../store/authStore';
import { useWbTicket } from '../context/WbTicketContext';
import { useWbLiveWeight } from '../context/WbLiveWeightContext';
import { useWbLiveFeed, type WbVehicleRecognizedEvent } from '../../../hooks/wb/useWbLiveFeed';
import { VehicleRecognitionPopup } from '../../../components/wb/VehicleRecognitionPopup';
import { useWbProducts, type UnitOfMeasure } from '../hooks/useWbProducts';
import { useWbProductGroups } from '../hooks/useWbProductGroups';
import { useWbHauliers } from '../hooks/useWbHauliers';
import { useWbDrivers } from '../hooks/useWbDrivers';
import { useWbVehicles } from '../hooks/useWbVehicles';
import type {
  TicketDetailDto,
  TicketDirection,
  TicketLineMode,
} from '../hooks/useWbTicket';
import type { AcquireLockResult, TicketLockDto } from '../hooks/useWbTickets';
import type { LiveBridge } from '../../../hooks/wb/useWbLiveBridges';
import { WbCustomerSearch } from '../components/WbCustomerSearch';
import { KioskLinePhoto } from './KioskLinePhoto';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { statusPillClass } from './statusPillClass';
import { KioskCombobox, type KioskComboOption } from './KioskCombobox';
import { KioskProductBrowser } from './KioskProductBrowser';
import { WbProductCombobox } from '../tickets/components/WbProductCombobox';
import { NotesBadge } from '../../../components/notes/NotesBadge';
import { NotesPanel } from '../../../components/notes/NotesPanel';
import styles from './kiosk.module.css';

interface DraftLine {
  uid: string;
  product_enc_id: string | null;
  product_name: string | null;
  mode: TicketLineMode;
  quantity: number;
  ewc_code: string;
  gross_weight_kg: number | null;
  tare_weight_kg: number | null;
  net_weight_kg: number | null;
  unit_price: number;
  unit_price_basis: 'contract' | 'price_band' | 'general_public' | 'manual_override' | null;
  override_reason: string | null;
  unit_of_measure: UnitOfMeasure | null;
}

// A capture's photo only resolves later via SSE (no synchronous "done"
// signal), so the loading indicator is delayed to avoid flicker on fast
// captures, and gives up after a ceiling so a silent agent/camera failure
// never leaves the button/thumbnail stuck spinning forever.
const CAPTURE_LOADING_DELAY_MS = 300;
const CAPTURE_LOADING_GIVE_UP_MS = 15_000;

let uidSeq = 0;
function newUid(): string {
  uidSeq += 1;
  return `kl-${uidSeq}-${Date.now()}`;
}

function calculateLineTotal(line: DraftLine): number {
  if (line.mode === 'weight') {
    // unit_price is always per tonne; convert kg to tonnes before multiplying.
    return ((line.net_weight_kg ?? 0) / 1000) * (line.unit_price ?? 0);
  }
  return (line.quantity ?? 0) * (line.unit_price ?? 0);
}

export interface KioskTicketWorkspaceProps {
  /** Always an existing ticket — created eagerly at KioskShell level. */
  readonly ticketEncId: string;
  readonly weighbridgeEncId: string;
  /** Bridge info lifted from KioskShell (shared across all tabs). */
  readonly activeBridge: LiveBridge | null;
  readonly activeBridgeId: number | null;
  /** Per-tab active line, persisted via useKioskTabs. */
  readonly activeLineUid: string | null;
  readonly setActiveLine: (uid: string | null) => void;
  /** Called after Print or Save & Close to remove this tab. */
  readonly onClose: () => void;
  /** Callback so KioskShell can update the tab label when number is assigned. */
  readonly onTicketLabel?: (label: string) => void;
}

export const KioskTicketWorkspace = memo(function KioskTicketWorkspace({
  ticketEncId,
  weighbridgeEncId,
  activeBridge,
  activeBridgeId,
  activeLineUid: initialActiveLineUid,
  setActiveLine: persistActiveLine,
  onClose,
  onTicketLabel,
}: KioskTicketWorkspaceProps) {
  const {
    ticket,
    hydrateFromTicket,
    patchTicket,
    saveTicket,
    resetTicket,
  } = useWbTicket();

  const productsQ = useWbProducts({ is_active: true });
  const productGroups = useWbProductGroups().data?.groups ?? [];
  const products = productsQ.data?.products ?? [];

  const { liveWeightKg, setLiveWeight } = useWbLiveWeight();
  const staffName = useAuthStore((s) => s.user?.name ?? null);

  // When no bridges are present at the shell level, subscribe to the direct WB
  // SSE feed. When bridges exist the BridgeSelectorRow at KioskShell level
  // manages the feed, so we disable our own subscription.
  const hasBridges = activeBridge !== null || activeBridgeId !== null;
  const captureBridgeBlocked = hasBridges && (activeBridge === null || !activeBridge.agent_online);
  const anprConfigured = activeBridge?.anpr_configured ?? false;

  const [customer, setCustomer] = useState<{ encId: string; name: string } | null>(null);
  const [direction, setDirection] = useState<TicketDirection>('inwards');
  const [notes, setNotes] = useState('');
  const [vehicle, setVehicle] = useState<{ encId: string; label: string } | null>(null);
  const [haulier, setHaulier] = useState<{ encId: string; label: string } | null>(null);
  const [driver, setDriver] = useState<{ encId: string; label: string } | null>(null);
  const [draftLines, setDraftLines] = useState<DraftLine[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingVoid, setConfirmingVoid] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [tareAutoFilled, setTareAutoFilled] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [lineSearch, setLineSearch] = useState('');
  const [addLineModalOpen, setAddLineModalOpen] = useState(false);
  const [lockedByOther, setLockedByOther] = useState<TicketLockDto | null>(null);
  const [lockHeld, setLockHeld] = useState(false);
  const [overrideReason, setOverrideReason] = useState<string>('');
  const [lineSnapshots, setLineSnapshots] = useState<
    Record<string, { gross?: string; tare?: string; material?: string }>
  >({});
  // Tracks which line uid+type is waiting for a snapshot SSE event — used as a
  // fallback match when an incoming event has no line_enc_id (e.g. an older
  // agent build, or a ticket-level-only capture with nothing to link to).
  const pendingCaptureRef = useRef<{ uid: string; type: 'gross' | 'tare' | 'material' } | null>(
    null,
  );
  // Loading indicator for a capture in flight (button spinner + photo-slot
  // spinner). The actual photo only resolves later via the SSE event, so this
  // is gated behind a delay (not shown immediately) to avoid flicker on fast
  // captures, and a give-up timeout (so a real camera/agent failure — which
  // has no error signal, only a missing event — never leaves the UI stuck).
  const [capturingByUid, setCapturingByUid] = useState<
    Record<string, Partial<Record<'gross' | 'tare' | 'material', boolean>>>
  >({});
  const captureTimersRef = useRef<
    Record<string, { show: ReturnType<typeof setTimeout>; giveUp: ReturnType<typeof setTimeout> }>
  >({});

  function setCapturing(uid: string, type: 'gross' | 'tare' | 'material', value: boolean): void {
    setCapturingByUid((prev) => ({ ...prev, [uid]: { ...prev[uid], [type]: value } }));
  }

  function clearCaptureTimers(key: string): void {
    const t = captureTimersRef.current[key];
    if (t) {
      clearTimeout(t.show);
      clearTimeout(t.giveUp);
      delete captureTimersRef.current[key];
    }
  }

  // Active line uid — initialised from parent tab state, persisted back on change.
  const [activeLineUid, setActiveLineUid] = useState<string | null>(initialActiveLineUid);

  const overflowRef = useRef<HTMLDivElement>(null);
  const overflowBtnRef = useRef<HTMLButtonElement>(null);
  const overflowPanelRef = useRef<HTMLDivElement>(null);
  const [overflowRect, setOverflowRect] = useState<DOMRect | null>(null);
  const { theme: kioskTheme } = useKioskTheme();

  const readOnly =
    ticket?.status === 'completed' ||
    ticket?.status === 'voided' ||
    lockedByOther !== null;

  const hauliersQ = useWbHauliers({ is_active: true, per_page: 100 });
  const vehiclesQ = useWbVehicles({ is_active: true, per_page: 100 });
  const driversQ = useWbDrivers({
    is_active: true,
    per_page: 100,
    ...(haulier ? { haulier_enc_id: haulier.encId } : {}),
  });

  const vehicleOptions: KioskComboOption[] = (vehiclesQ.data?.vehicles ?? []).map((v) => ({
    id: v.enc_id,
    label: v.registration,
    sublabel: v.haulier?.name ?? null,
  }));
  const haulierOptions: KioskComboOption[] = (hauliersQ.data?.hauliers ?? []).map((h) => ({
    id: h.enc_id,
    label: h.name,
    sublabel: h.code,
  }));
  const driverOptions: KioskComboOption[] = (driversQ.data?.drivers ?? []).map((d) => ({
    id: d.enc_id,
    label: d.name,
    sublabel: d.licence_number ?? d.default_haulier?.name ?? null,
  }));

  // Encrypted per-line reference to send in set-active-ticket so a capture
  // links to this specific line (gross_image_id/tare_image_id/photo_image_id)
  // instead of only the ticket as a whole. Undefined until this line's
  // patchTicket round-trip resolves — captureLineWeight sends it when present
  // and omits it otherwise (fail-open: an unlinked ticket-level snapshot is
  // the graceful degradation, not a disabled button — see Amendment A).
  const lineEncIdByUid = useMemo(() => {
    const m: Record<string, string> = {};
    (ticket?.lines ?? []).forEach((l) => {
      const uid = l.uid ?? `srv-${String(l.id)}`;
      m[uid] = l.line_enc_id;
    });
    return m;
  }, [ticket?.lines]);

  // Reverse of the above — lets a live ticket_snapshot_saved SSE event (which
  // carries line_enc_id, not uid) update the correct lineSnapshots[uid] entry.
  const lineUidByEncId = useMemo(() => {
    const m: Record<string, string> = {};
    Object.entries(lineEncIdByUid).forEach(([uid, encId]) => {
      m[encId] = uid;
    });
    return m;
  }, [lineEncIdByUid]);

  // Raw storage keys for previously-captured photos (from the last ticket
  // fetch) — the fallback source for KioskLinePhoto when this session hasn't
  // received a fresher direct URL via SSE yet (e.g. right after a refresh).
  const lineImageKeysByUid = useMemo(() => {
    const m: Record<string, { gross: string | null; tare: string | null; material: string | null }> = {};
    (ticket?.lines ?? []).forEach((l) => {
      const uid = l.uid ?? `srv-${String(l.id)}`;
      m[uid] = {
        gross: l.gross_image_url,
        tare: l.tare_image_url,
        material: l.photo_image_url,
      };
    });
    return m;
  }, [ticket?.lines]);

  const lineSearchResults = useMemo(() => {
    const q = lineSearch.trim().toLowerCase();
    if (!q) return [];
    return products
      .filter(
        (p) =>
          p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [lineSearch, products]);

  // Sync active line changes back to parent tab state.
  // Use a ref so re-renders that produce a new inline persistActiveLine reference
  // (from KioskRoot's map) don't re-trigger this effect and cause an infinite loop.
  const persistActiveLineRef = useRef(persistActiveLine);
  persistActiveLineRef.current = persistActiveLine;
  useEffect(() => {
    persistActiveLineRef.current(activeLineUid);
  }, [activeLineUid]);

  // Ensure activeLineUid stays valid as lines are added/removed.
  useEffect(() => {
    if (draftLines.length === 0) {
      setActiveLineUid(null);
      return;
    }
    const exists = draftLines.some((l) => l.uid === activeLineUid);
    if (!exists) {
      setActiveLineUid(draftLines[draftLines.length - 1]?.uid ?? null);
    }
  }, [draftLines, activeLineUid]);

  // Close overflow on click outside.
  useEffect(() => {
    if (!overflowOpen) return undefined;
    function handleOutside(e: MouseEvent) {
      const target = e.target as Node;
      const inPanel = overflowPanelRef.current && overflowPanelRef.current.contains(target);
      const onBtn = overflowRef.current && overflowRef.current.contains(target);
      if (!inPanel && !onBtn) {
        setOverflowOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [overflowOpen]);

  // Hydrate from the server on mount. ticketEncId is always an existing ticket.
  useEffect(() => {
    void (async () => {
      try {
        const res = await apiClient.get<{ ticket: TicketDetailDto }>(
          endpoints.wb.ticket(ticketEncId),
        );
        const t = res.data.ticket;
        hydrateFromTicket(t);
        if (t.customer) setCustomer({ encId: t.customer.enc_id, name: t.customer.name });
        setDirection(t.direction);
        setNotes(t.notes ?? '');
        if (t.vehicle) setVehicle({ encId: t.vehicle.enc_id, label: t.vehicle.registration });
        setTareAutoFilled(t.tare_method === 'stored' && t.tare_weight_kg !== null);
        if (t.haulier) setHaulier({ encId: t.haulier.enc_id, label: t.haulier.name });
        if (t.driver) setDriver({ encId: t.driver.enc_id, label: t.driver.name });
        const lines: DraftLine[] = t.lines.map((l) => ({
          uid: l.uid ?? `srv-${l.id}`,
          product_enc_id: l.product?.enc_id ?? null,
          product_name: l.product?.name ?? l.product_name_snapshot ?? null,
          mode: l.mode,
          quantity: l.quantity,
          ewc_code: l.ewc_code_snapshot ?? '',
          gross_weight_kg: l.gross_weight_kg ?? null,
          tare_weight_kg: l.tare_weight_kg ?? null,
          net_weight_kg: l.net_weight_kg ?? null,
          unit_price: l.unit_price ?? 0,
          unit_price_basis: l.unit_price_basis ?? null,
          override_reason: l.override_reason ?? null,
          unit_of_measure: products.find((p) => p.enc_id === (l.product?.enc_id ?? ''))?.unit_of_measure ?? null,
        }));
        setDraftLines(lines);
        // Initialise active line: use stored uid if still present, else first.
        setActiveLineUid((prev) => {
          const uid = prev ?? initialActiveLineUid;
          return uid && lines.some((l) => l.uid === uid) ? uid : (lines[lines.length - 1]?.uid ?? null);
        });
        // Report label for tab bar — prefer customer name so reopened tickets
        // show a meaningful label before a ticket number is assigned.
        onTicketLabel?.(t.customer?.name ?? t.ticket_number ?? 'Draft');
        // Lock acquire (editable tickets only).
        if (t.status === 'draft' || t.status === 'open') {
          try {
            const lockRes = await apiClient.post<AcquireLockResult>(
              endpoints.wb.ticketLock(ticketEncId),
              { origin: 'kiosk' },
            );
            if (lockRes.data.acquired) setLockHeld(true);
            else if (lockRes.data.lock) setLockedByOther(lockRes.data.lock);
          } catch {
            /* lock service unavailable */
          }
        }
      } catch {
        setError('Could not load ticket.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketEncId]);

  // Release lock and reset context on unmount.
  useEffect(() => {
    return () => {
      resetTicket();
      void apiClient.delete(endpoints.wb.ticketLock(ticketEncId)).catch(() => {});
    };
  }, [ticketEncId, resetTicket]);

  // 30s lock heartbeat.
  useEffect(() => {
    if (!lockHeld) return undefined;
    const interval = setInterval(() => {
      void apiClient
        .post(endpoints.wb.ticketLockHeartbeat(ticketEncId), {})
        .catch(() => {});
    }, 30_000);
    return () => clearInterval(interval);
  }, [ticketEncId, lockHeld]);

  // Auto-recover from a stale lock without requiring a manual refresh: locks
  // expire server-side (5min TTL — see wb/locks/locks.queries.ts), so a
  // crashed session's lock naturally goes stale on its own. Once THIS lock's
  // own expires_at passes, retry acquisition automatically. Floored at 1s so
  // client/server clock skew never causes an immediate retry storm. If the
  // ticket is still held (someone else's heartbeat renewed it in the
  // meantime), the retry response carries the NEW lock and this effect
  // re-schedules against its expiry.
  useEffect(() => {
    if (lockedByOther === null) return undefined;
    if (ticket === null || (ticket.status !== 'draft' && ticket.status !== 'open')) return undefined;
    // Floored at 1s (clock-skew safety) and capped at 10min (locks' real TTL
    // is 5min — a further-out expires_at is a test fixture, not a case worth
    // scheduling a literal multi-year setTimeout for, which overflows Node's
    // 32-bit timer limit anyway).
    const rawDelayMs = new Date(lockedByOther.expires_at).getTime() - Date.now() + 1000;
    const delayMs = Math.min(Math.max(1000, rawDelayMs), 10 * 60 * 1000);
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const lockRes = await apiClient.post<AcquireLockResult>(
            endpoints.wb.ticketLock(ticketEncId),
            { origin: 'kiosk' },
          );
          if (lockRes.data.acquired) {
            setLockHeld(true);
            setLockedByOther(null);
          } else if (lockRes.data.lock) {
            setLockedByOther(lockRes.data.lock);
          }
        } catch {
          // Lock service unavailable — leave state as-is. The operator can
          // still close/reopen the tab as a manual fallback.
        }
      })();
    }, delayMs);
    return () => clearTimeout(timer);
  }, [lockedByOther, ticket, ticketEncId]);

  // Sync price fields back from PATCH echo so resolved pricing is shown.
  // Returns prev unchanged when no priced fields have changed, bailing out of the re-render.
  useEffect(() => {
    if (!ticket) return;
    setDraftLines((prev) => {
      let changed = false;
      const next = prev.map((dl) => {
        const sl = ticket.lines.find(
          (l) => (l.uid ?? `srv-${String(l.id)}`) === dl.uid,
        );
        if (!sl) return dl;
        const price  = sl.unit_price       ?? dl.unit_price;
        const basis  = sl.unit_price_basis ?? dl.unit_price_basis;
        const reason = sl.override_reason  ?? dl.override_reason;
        if (
          price  === dl.unit_price       &&
          basis  === dl.unit_price_basis &&
          reason === dl.override_reason
        ) {
          return dl;
        }
        changed = true;
        return { ...dl, unit_price: price, unit_price_basis: basis, override_reason: reason };
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.lines]);

  // Reset override reason input when the active line changes.
  useEffect(() => {
    const line = draftLines.find((l) => l.uid === activeLineUid);
    setOverrideReason(line?.override_reason ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLineUid]);

  const [vehicleRecognizedEvent, setVehicleRecognizedEvent] =
    useState<WbVehicleRecognizedEvent | null>(null);
  const dismissVehiclePopup = () => setVehicleRecognizedEvent(null);

  // Single consolidated WB SSE feed for this tab: weight updates (only when no
  // bridge selector is active — the shell's BridgeSelectorRow owns that feed
  // otherwise), always-on snapshot events, and always-on vehicle recognition.
  useWbLiveFeed({
    enabled: !readOnly,
    onWeightUpdate: (ev) => {
      if (hasBridges || !weighbridgeEncId) return;
      setLiveWeight(ev.value, ev.stable);
    },
    onTicketSnapshot: (ev) => {
      if (!ticket || ev.ticket_enc_id !== ticket.enc_id) return;
      if (ev.label !== 'gross' && ev.label !== 'tare' && ev.label !== 'material') return;
      // Amendment C: prefer the durable line_enc_id the event carries (works
      // regardless of which tab/click armed it) over the in-memory
      // pendingCaptureRef fallback (only valid for THIS tab's own pending
      // click, and only until the next arm overwrites it).
      const uidFromEncId = ev.line_enc_id ? lineUidByEncId[ev.line_enc_id] : undefined;
      const pending = pendingCaptureRef.current;
      const uidFromPending = pending && ev.label === pending.type ? pending.uid : undefined;
      const targetUid = uidFromEncId ?? uidFromPending;
      if (!targetUid) return;
      setLineSnapshots((prev) => ({
        ...prev,
        [targetUid]: {
          ...prev[targetUid],
          [ev.label]: ev.image_url,
        },
      }));
      clearCaptureTimers(`${targetUid}:${ev.label}`);
      setCapturing(targetUid, ev.label, false);
      if (pending && pending.uid === targetUid && pending.type === ev.label) {
        pendingCaptureRef.current = null;
      }
    },
    onVehicleRecognized: (e) => setVehicleRecognizedEvent(e),
  });

  // -- Field handlers -------------------------------------------------------

  function onPickVehicle(id: string | null): void {
    const v = (vehiclesQ.data?.vehicles ?? []).find((x) => x.enc_id === id) ?? null;
    setVehicle(id ? { encId: id, label: v?.registration ?? vehicle?.label ?? '' } : null);
    const hasFreshTare = id !== null && v?.stored_tare_kg != null && v.tare_status === 'fresh';
    if (ticket) {
      const patch: { vehicle_enc_id: string | null; tare_weight_kg?: number } = {
        vehicle_enc_id: id,
      };
      if (hasFreshTare && v?.stored_tare_kg != null) patch.tare_weight_kg = v.stored_tare_kg;
      patchTicket(patch);
    }
    setTareAutoFilled(hasFreshTare);
  }

  function onPickHaulier(id: string | null): void {
    const h = (hauliersQ.data?.hauliers ?? []).find((x) => x.enc_id === id) ?? null;
    setHaulier(id ? { encId: id, label: h?.name ?? haulier?.label ?? '' } : null);
    setDriver(null);
    if (ticket) patchTicket({ haulier_enc_id: id, driver_enc_id: null });
  }

  function onPickDriver(id: string | null): void {
    const d = (driversQ.data?.drivers ?? []).find((x) => x.enc_id === id) ?? null;
    setDriver(id ? { encId: id, label: d?.name ?? driver?.label ?? '' } : null);
    if (ticket) patchTicket({ driver_enc_id: id });
  }

  // -- Line management ------------------------------------------------------

  function buildLineInputs(lines: DraftLine[]) {
    return lines
      .filter((l) => l.product_enc_id)
      .map((l) => ({
        uid: l.uid,
        product_enc_id: l.product_enc_id as string,
        mode: l.mode,
        quantity: l.mode === 'weight' ? Math.max(0, l.net_weight_kg ?? 0) : Math.max(0, l.quantity ?? 0),
        unit_price: l.unit_price_basis === 'manual_override' ? l.unit_price : 0,
        ...(l.unit_price_basis ? { unit_price_basis: l.unit_price_basis } : {}),
        ...(l.unit_price_basis === 'manual_override' && l.override_reason
          ? { override_reason: l.override_reason }
          : {}),
        ...(l.ewc_code ? { ewc_code: l.ewc_code } : {}),
        ...(l.gross_weight_kg != null ? { gross_weight_kg: l.gross_weight_kg } : {}),
        ...(l.tare_weight_kg != null ? { tare_weight_kg: l.tare_weight_kg } : {}),
        ...(l.net_weight_kg != null ? { net_weight_kg: l.net_weight_kg } : {}),
      }));
  }

  function pushLines(next: DraftLine[]): void {
    setDraftLines(next);
    if (!ticket) return;
    patchTicket({ lines: buildLineInputs(next) });
  }

  function updateLine(uid: string, patch: Partial<DraftLine>): void {
    pushLines(draftLines.map((l) => (l.uid === uid ? { ...l, ...patch } : l)));
  }

  function onPickProduct(uid: string, encId: string | null): void {
    const product = products.find((p) => p.enc_id === encId) ?? null;
    updateLine(uid, {
      product_enc_id: encId,
      product_name: product?.name ?? null,
      mode: product?.mode === 'unit' ? 'unit' : 'weight',
      unit_of_measure: product?.unit_of_measure ?? null,
      ewc_code:
        draftLines.find((l) => l.uid === uid)?.ewc_code || product?.ewc_code || '',
    });
  }

  function addLineWithProduct(encId: string | null): void {
    if (!encId) return;
    const product = products.find((p) => p.enc_id === encId);
    const uid = newUid();
    pushLines([
      ...draftLines,
      {
        uid,
        product_enc_id: encId,
        product_name: product?.name ?? null,
        mode: product?.mode === 'unit' ? 'unit' : 'weight',
        quantity: 0,
        ewc_code: product?.ewc_code ?? '',
        gross_weight_kg: null,
        tare_weight_kg: null,
        net_weight_kg: null,
        unit_price: 0,
        unit_price_basis: null,
        override_reason: null,
        unit_of_measure: product?.unit_of_measure ?? null,
      },
    ]);
    setActiveLineUid(uid);
    setLineSearch('');
    setAddLineModalOpen(false);
  }

  async function captureLineWeight(
    uid: string,
    type: 'gross' | 'tare' | 'material',
  ): Promise<void> {
    const line = draftLines.find((l) => l.uid === uid);
    if (!line) return;

    // Notify Agent which ticket (+ line, when resolved) + weight type to
    // snapshot next. Amendment A: capture is ALWAYS enabled — line_enc_id is
    // sent when this line has round-tripped to the server and omitted
    // otherwise. The claim path already fails open on a missing line
    // reference (keeps an unlinked ticket-level snapshot), so there is no
    // dead-button window to avoid by gating on it here.
    if (ticket?.enc_id && activeBridgeId !== null) {
      pendingCaptureRef.current = { uid, type };
      const lineEncId = lineEncIdByUid[uid];
      const timerKey = `${uid}:${type}`;
      clearCaptureTimers(timerKey); // a rapid re-click supersedes any prior wait
      captureTimersRef.current[timerKey] = {
        show: setTimeout(() => setCapturing(uid, type, true), CAPTURE_LOADING_DELAY_MS),
        giveUp: setTimeout(() => {
          clearCaptureTimers(timerKey);
          setCapturing(uid, type, false);
        }, CAPTURE_LOADING_GIVE_UP_MS),
      };
      try {
        await apiClient.post(endpoints.wb.setActiveTicket, {
          weighbridge_id: activeBridgeId,
          ticket_enc_id: ticket.enc_id,
          weight_type: type,
          ...(lineEncId ? { line_enc_id: lineEncId } : {}),
          ...(staffName ? { staff_name: staffName } : {}),
        });
      } catch {
        // The arm request itself failed — nothing is in flight on the Agent,
        // so give up on the loading indicator now rather than waiting out the
        // full timeout. Capture proceeds regardless (matches prior behaviour).
        clearCaptureTimers(timerKey);
        setCapturing(uid, type, false);
      }
    }

    if (type === 'material') return; // no numeric weight field to update
    // No live reading to record (e.g. the operator manually typed the weight
    // into the input box instead of reading it off the bridge) — the arm +
    // capture above still ran, so this is a manual-capture-photo-only click,
    // not a weight-value overwrite.
    if (liveWeightKg == null) return;

    const gross = type === 'gross' ? liveWeightKg : line.gross_weight_kg;
    const tare = type === 'tare' ? liveWeightKg : line.tare_weight_kg;
    // Task 3: net computes immediately on gross capture (tare defaults to 0).
    const net =
      gross != null
        ? Math.max(0, Math.round((gross - (tare ?? 0)) * 1000) / 1000)
        : null;
    if (type === 'tare') setTareAutoFilled(false);
    updateLine(uid, { gross_weight_kg: gross, tare_weight_kg: tare, net_weight_kg: net });
  }

  // -- Price override -------------------------------------------------------

  function togglePriceOverride(): void {
    if (!activeLine) return;
    if (activeLine.unit_price_basis === 'manual_override') {
      updateLine(activeLine.uid, { unit_price_basis: null, override_reason: null });
      setOverrideReason('');
    } else {
      updateLine(activeLine.uid, { unit_price_basis: 'manual_override' });
    }
  }

  function handleManualPrice(value: number): void {
    if (!activeLine) return;
    setDraftLines((prev) =>
      prev.map((l) => (l.uid === activeLine.uid ? { ...l, unit_price: value } : l)),
    );
  }

  function handleOverrideReasonBlur(): void {
    if (!activeLine) return;
    updateLine(activeLine.uid, { override_reason: overrideReason });
  }

  // -- Actions --------------------------------------------------------------

  function handleScanPlate(): void {
    if (!activeBridge || !anprConfigured || isScanning) return;
    setIsScanning(true);
    void apiClient
      .post(endpoints.wb.triggerAnpr, { weighbridge_id: activeBridge.weighbridge_id })
      .catch(() => {})
      .finally(() => {
        setTimeout(() => setIsScanning(false), 5000);
      });
  }

  async function onSaveDraft(): Promise<void> {
    if (!ticket) return;
    patchTicket({ notes: notes || null });
    setBusy(true);
    setError(null);
    try {
      await saveTicket();
      // Tickets are 'open' by default now — this button doubles as the
      // explicit reverse transition ("un-finalize") when the ticket has
      // already been opened. Idempotent no-op while already 'draft'.
      // Closes the tab immediately on an actual transition — the same
      // "it visibly happened" feedback Mark as Open already gives; a plain
      // save while already draft keeps the tab open for continued editing.
      if (ticket.status === 'open') {
        await apiClient.patch(endpoints.wb.ticket(ticket.enc_id), { status: 'draft' });
        onClose();
      }
    } catch {
      setError('Could not save as draft.');
    } finally {
      setBusy(false);
    }
  }

  async function onMarkOpen(): Promise<void> {
    if (!ticket) return;
    patchTicket({ notes: notes || null });
    setBusy(true);
    setError(null);
    try {
      await saveTicket();
      await apiClient.patch(endpoints.wb.ticket(ticket.enc_id), { status: 'open' });
      onClose();
    } catch {
      setError('Could not mark the ticket open.');
    } finally {
      setBusy(false);
    }
  }

  async function onPrint(): Promise<void> {
    if (!ticket) return;
    setBusy(true);
    setError(null);
    try {
      const url = endpoints.wb.ticketPdf(ticket.enc_id, 'wb_ticket');
      const res = await apiClient.raw.get(url, { responseType: 'blob' });
      const objectUrl = URL.createObjectURL(res.data as Blob);
      const opened = window.open(objectUrl, '_blank', 'noopener,noreferrer');
      if (opened === null) {
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = `wb_ticket-${ticket.enc_id}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      onClose();
    } catch {
      setError('Could not generate the ticket PDF.');
    } finally {
      setBusy(false);
    }
  }

  async function onVoid(): Promise<void> {
    if (!ticket) return;
    setBusy(true);
    setError(null);
    try {
      await apiClient.post(endpoints.wb.ticketVoid(ticket.enc_id), {
        void_reason: 'Voided at kiosk',
      });
      onClose();
    } catch {
      setError('Could not void the ticket.');
      setConfirmingVoid(false);
    } finally {
      setBusy(false);
    }
  }

  // -- Derived values -------------------------------------------------------

  const activeLine = draftLines.find((l) => l.uid === activeLineUid) ?? null;

  const lineSource = ticket?.lines ?? [];
  const netTotal = (ticket ? lineSource : draftLines).reduce(
    (sum, l) => sum + (l.net_weight_kg ?? 0),
    0,
  );
  const lineCount = ticket ? lineSource.length : draftLines.length;

  const status = ticket?.status ?? 'draft';
  const canSaveDraft = !readOnly && status === 'open' && ticket !== null;
  const canMarkOpen = !readOnly && status === 'draft' && ticket !== null;
  const canPrint = ticket !== null && (status === 'open' || status === 'completed');
  const canVoid = !busy && status === 'open';
  const canDelete = !readOnly && (status === 'draft' || status === 'open');

  // -- Render ---------------------------------------------------------------

  return (
    <div className={styles.workspace} data-testid="kiosk-workspace">
      {lockedByOther ? (
        <div
          className={styles.kioskLockBanner}
          style={{ gridColumn: '1 / -1' }}
          data-testid="kiosk-lock-banner"
        >
          🔒 This ticket is being edited by{' '}
          <strong>{lockedByOther.locked_by_name}</strong>. You are viewing in
          read-only mode.
        </div>
      ) : null}

      {/* LEFT — details header (⋯) + customer + vehicle + notes */}
      <div className={styles.wsLeft}>
        <div className={styles.wsColHeader}>
          <h3 className={styles.wsColTitle}>Details</h3>
          <div className={styles.overflowAnchor} ref={overflowRef}>
            <button
              ref={overflowBtnRef}
              type="button"
              className={styles.overflowBtn}
              onClick={() => {
                if (overflowBtnRef.current) {
                  setOverflowRect(overflowBtnRef.current.getBoundingClientRect());
                }
                setOverflowOpen((o) => !o);
              }}
              aria-label="More options"
              title="More options"
            >
              ⋯
            </button>
          </div>
          {createPortal(
            <div
              ref={overflowPanelRef}
              className={styles.overflowPanel}
              data-kiosk-theme={kioskTheme}
              style={{
                display: overflowOpen ? 'flex' : 'none',
                position: 'fixed',
                top: overflowRect ? Math.min(overflowRect.bottom + 6, window.innerHeight - 520 - 8) : 0,
                right: overflowRect ? window.innerWidth - overflowRect.right : 0,
                maxHeight: overflowRect
                  ? Math.min(window.innerHeight - overflowRect.bottom - 16, 520)
                  : 520,
              }}
            >
              <div className={styles.overflowPanelHeader}>
                <span className={styles.label}>Options</span>
                <button
                  type="button"
                  className={styles.overflowCloseBtn}
                  onClick={() => setOverflowOpen(false)}
                  aria-label="Close options"
                >
                  ✕
                </button>
              </div>

              <div className={styles.overflowSection}>
                <label className={styles.label}>Direction</label>
                <div className={styles.toggleRow}>
                  {(['inwards', 'outwards'] as const).map((d) => (
                    <button
                      key={d}
                      type="button"
                      className={`${styles.toggleCard} ${
                        direction === d ? styles.toggleCardActive : ''
                      }`}
                      disabled={!ticket || readOnly}
                      onClick={() => {
                        setDirection(d);
                        patchTicket({ direction: d });
                      }}
                    >
                      {d === 'inwards' ? 'Inbound' : 'Outbound'}
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.overflowSection}>
                <label className={styles.label}>Haulier</label>
                <KioskCombobox
                  options={haulierOptions}
                  value={haulier?.encId ?? null}
                  selectedLabelFallback={haulier?.label ?? null}
                  onChange={onPickHaulier}
                  loading={hauliersQ.isLoading}
                  disabled={readOnly}
                  placeholder="Search hauliers…"
                  ariaLabel="Haulier"
                  emptyText="No hauliers match."
                  data-testid="kiosk-haulier-combobox"
                />
              </div>

              <div className={styles.overflowSection}>
                <label className={styles.label}>Driver</label>
                <KioskCombobox
                  options={driverOptions}
                  value={driver?.encId ?? null}
                  selectedLabelFallback={driver?.label ?? null}
                  onChange={onPickDriver}
                  loading={driversQ.isLoading}
                  disabled={readOnly}
                  placeholder={haulier ? `Search ${haulier.label} drivers…` : 'Search drivers…'}
                  ariaLabel="Driver"
                  emptyText={haulier ? 'No drivers for this haulier.' : 'No drivers match.'}
                  data-testid="kiosk-driver-combobox"
                />
              </div>

              <div className={styles.overflowSection}>
                <button
                  type="button"
                  className={styles.kioskScanPlateBtn}
                  onClick={handleScanPlate}
                  disabled={!anprConfigured || readOnly || isScanning}
                  title={
                    !anprConfigured
                      ? 'ANPR camera not configured for this bridge'
                      : 'Scan number plate with ANPR camera'
                  }
                  data-testid="kiosk-scan-plate-btn"
                >
                  <ScanLine size={14} />
                  {isScanning ? 'Scanning…' : 'Scan Plate'}
                </button>
              </div>

              {ticket && (
                <div className={styles.overflowSection}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <label className={styles.label} style={{ margin: 0 }}>Staff Notes</label>
                    <NotesBadge
                      count={ticket.notes_count ?? 0}
                      onClick={() => {
                        setNotesOpen(true);
                        setOverflowOpen(false);
                      }}
                    />
                  </div>
                </div>
              )}

              <div className={styles.overflowSection}>
                {confirmingVoid ? (
                  <div className={styles.voidConfirm}>
                    <span className={styles.muted}>Void this ticket?</span>
                    <div className={styles.voidConfirmRow}>
                      <button
                        type="button"
                        className={styles.secondaryBtn}
                        onClick={() => setConfirmingVoid(false)}
                        disabled={busy}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className={styles.dangerBtn}
                        onClick={() => void onVoid()}
                        disabled={busy}
                        data-testid="kiosk-void-confirm"
                      >
                        Confirm Void
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={styles.dangerBtn}
                    style={{ width: '100%', minWidth: 0 }}
                    onClick={() => setConfirmingVoid(true)}
                    disabled={!canVoid}
                    data-testid="kiosk-void"
                  >
                    Void
                  </button>
                )}
              </div>

            </div>,
            document.body,
          )}
        </div>

        {ticket ? (
          <span className={statusPillClass(status)} data-testid="kiosk-status-banner">
            {status}
          </span>
        ) : null}

        <div className={styles.field} style={{ maxWidth: '100%' }}>
          <label className={styles.label}>Customer</label>
          <WbCustomerSearch
            value={customer?.encId ?? null}
            selectedName={customer?.name ?? null}
            ariaLabel="Customer"
            disabled={readOnly || ticket !== null}
            onChange={(encId, name) => {
              setCustomer(encId ? { encId, name: name ?? '' } : null);
              if (ticket && encId) patchTicket({ customer_enc_id: encId });
            }}
          />
        </div>

        <div className={styles.field} style={{ maxWidth: '100%' }}>
          <label className={styles.label}>Vehicle</label>
          <KioskCombobox
            options={vehicleOptions}
            value={vehicle?.encId ?? null}
            selectedLabelFallback={vehicle?.label ?? null}
            onChange={onPickVehicle}
            loading={vehiclesQ.isLoading}
            disabled={readOnly}
            placeholder="Search registration…"
            ariaLabel="Vehicle"
            emptyText="No vehicles match."
            data-testid="kiosk-vehicle-combobox"
          />
          {tareAutoFilled ? (
            <span
              className={styles.kioskTareAutoFilled}
              data-testid="kiosk-tare-autofilled-hint"
            >
              Loaded from vehicle record
            </span>
          ) : null}
        </div>

        <div className={styles.field} style={{ maxWidth: '100%' }}>
          <label className={styles.label}>Notes</label>
          <textarea
            className={styles.input}
            style={{ minHeight: 72, paddingTop: '0.6rem' }}
            value={notes}
            disabled={readOnly}
            data-testid="kiosk-notes-textarea"
            onChange={(e) => {
              setNotes(e.target.value);
              if (ticket) patchTicket({ notes: e.target.value || null });
            }}
          />
        </div>
      </div>

      {/* MAIN — compact line list (left) + active line detail (right) */}
      <div className={styles.wsMain}>
        <div className={styles.lineColumn}>
          {/* Product search + browse row — always at top */}
          {!readOnly ? (
            <div className={styles.lineAddRow}>
              <div className={styles.lineSearchWrap}>
                <input
                  className={styles.lineSearchInput}
                  placeholder="Search product…"
                  value={lineSearch}
                  onChange={(e) => setLineSearch(e.target.value)}
                  aria-label="Search product to add"
                />
                {lineSearchResults.length > 0 ? (
                  <div className={styles.lineSearchDropdown}>
                    {lineSearchResults.map((p) => (
                      <button
                        key={p.enc_id}
                        type="button"
                        className={styles.lineSearchOption}
                        onClick={() => addLineWithProduct(p.enc_id)}
                      >
                        {p.name}
                        {p.code ? (
                          <span className={styles.muted}> ({p.code})</span>
                        ) : null}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                className={styles.secondaryBtn}
                style={{ minWidth: 56 }}
                onClick={() => setAddLineModalOpen(true)}
                aria-label="Browse products"
                title="Browse all products"
              >
                +
              </button>
            </div>
          ) : null}

          {draftLines.length === 0 ? (
            <p className={styles.muted}>No lines yet.</p>
          ) : null}

          {/* Compact line pills — click to select, all details in right panel */}
          {draftLines.map((l, idx) => {
            const isActive = l.uid === activeLineUid;
            const netLabel =
              l.mode === 'unit'
                ? l.quantity > 0
                  ? `${l.quantity} units`
                  : '—'
                : l.net_weight_kg != null
                  ? `${l.net_weight_kg.toLocaleString()} kg`
                  : '—';
            return (
              <div
                key={l.uid}
                className={`${styles.linePill} ${isActive ? styles.linePillActive : styles.linePillInactive}`}
                onClick={() => setActiveLineUid(l.uid)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setActiveLineUid(l.uid);
                }}
              >
                <span className={styles.linePillNo}>{idx + 1}</span>
                <span className={styles.linePillName}>
                  {l.product_name ?? <em className={styles.muted}>No product</em>}
                </span>
                <span className={styles.linePillNet}>{netLabel}</span>
              </div>
            );
          })}
        </div>

        {/* Line detail panel — full controls for the selected line */}
        <div className={styles.lineDetailPanel}>
          <div className={styles.liveReadout} data-testid="kiosk-live-readout">
            <span className={styles.topMetaLabel}>Live weight</span>
            <span className={styles.liveReadoutValue}>
              {liveWeightKg != null
                ? `${liveWeightKg.toLocaleString()} kg`
                : '— no reading'}
            </span>
          </div>

          {activeLine ? (
            <>
              <WbProductCombobox
                products={products}
                groups={productGroups}
                value={activeLine.product_enc_id}
                selectedNameFallback={activeLine.product_name}
                onChange={(encId) => onPickProduct(activeLine.uid, encId)}
                ariaLabel="Product"
                disabled={readOnly}
              />
              {activeLine.mode === 'weight' ? (
                <div className={styles.lineWeights}>
                  <div className={styles.weightRow}>
                    {/* Gross */}
                    <div className={styles.weightInputGroup}>
                      <label className={styles.weightInputLabel}>Gross (kg)</label>
                      <div className={styles.weightInputWithBtn}>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className={styles.weightInput}
                          value={activeLine.gross_weight_kg ?? 0}
                          disabled={readOnly}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value) || 0;
                            updateLine(activeLine.uid, {
                              gross_weight_kg: v,
                              net_weight_kg: Math.max(0, v - (activeLine.tare_weight_kg ?? 0)),
                            });
                          }}
                        />
                        {!readOnly ? (
                          <button
                            type="button"
                            className={styles.captureBtn}
                            onClick={() => void captureLineWeight(activeLine.uid, 'gross')}
                            disabled={captureBridgeBlocked || capturingByUid[activeLine.uid]?.gross === true}
                            aria-busy={capturingByUid[activeLine.uid]?.gross === true}
                            title={
                              captureBridgeBlocked
                                ? 'Select a bridge'
                                : liveWeightKg == null
                                  ? 'Capture a photo for the manually entered gross weight'
                                  : 'Capture current weight as gross'
                            }
                            aria-label="Capture gross"
                          >
                            {capturingByUid[activeLine.uid]?.gross ? (
                              <Spinner size="sm" label="Capturing gross photo" />
                            ) : (
                              '⚖ G'
                            )}
                          </button>
                        ) : null}
                      </div>
                      <KioskLinePhoto
                        scopeKey={ticketEncId}
                        label="Gross"
                        directUrl={lineSnapshots[activeLine.uid]?.gross}
                        storageKey={
                          lineSnapshots[activeLine.uid]?.gross
                            ? undefined
                            : (lineImageKeysByUid[activeLine.uid]?.gross ?? undefined)
                        }
                        capturing={capturingByUid[activeLine.uid]?.gross === true}
                      />
                    </div>

                    {/* Tare */}
                    <div className={styles.weightInputGroup}>
                      <label className={styles.weightInputLabel}>Tare (kg)</label>
                      <div className={styles.weightInputWithBtn}>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className={styles.weightInput}
                          value={activeLine.tare_weight_kg ?? 0}
                          disabled={readOnly}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value) || 0;
                            updateLine(activeLine.uid, {
                              tare_weight_kg: v,
                              net_weight_kg: Math.max(0, (activeLine.gross_weight_kg ?? 0) - v),
                            });
                          }}
                        />
                        {!readOnly ? (
                          <button
                            type="button"
                            className={styles.captureBtn}
                            onClick={() => void captureLineWeight(activeLine.uid, 'tare')}
                            disabled={captureBridgeBlocked || capturingByUid[activeLine.uid]?.tare === true}
                            aria-busy={capturingByUid[activeLine.uid]?.tare === true}
                            title={
                              captureBridgeBlocked
                                ? 'Select a bridge'
                                : liveWeightKg == null
                                  ? 'Capture a photo for the manually entered tare weight'
                                  : 'Capture current weight as tare'
                            }
                            aria-label="Capture tare"
                          >
                            {capturingByUid[activeLine.uid]?.tare ? (
                              <Spinner size="sm" label="Capturing tare photo" />
                            ) : (
                              '⚖ T'
                            )}
                          </button>
                        ) : null}
                      </div>
                      <KioskLinePhoto
                        scopeKey={ticketEncId}
                        label="Tare"
                        directUrl={lineSnapshots[activeLine.uid]?.tare}
                        storageKey={
                          lineSnapshots[activeLine.uid]?.tare
                            ? undefined
                            : (lineImageKeysByUid[activeLine.uid]?.tare ?? undefined)
                        }
                        capturing={capturingByUid[activeLine.uid]?.tare === true}
                      />
                    </div>
                  </div>

                  {/* Net — full width below */}
                  <div className={styles.netWeightRow}>
                    <span className={styles.weightInputLabel}>Net (kg)</span>
                    <span className={styles.netWeightValue}>
                      {activeLine.net_weight_kg != null
                        ? activeLine.net_weight_kg.toFixed(2)
                        : '0.00'}
                    </span>
                  </div>
                </div>
              ) : null}

              {/* Price section */}
              <div className={styles.priceSection}>
                <div className={styles.priceRow}>
                  <span className={styles.priceLabel}>
                    {activeLine.unit_price_basis === 'contract'
                      ? 'Contract price'
                      : activeLine.unit_price_basis === 'price_band'
                        ? 'Price band'
                        : activeLine.unit_price_basis === 'general_public'
                          ? 'General public'
                          : activeLine.unit_price_basis === 'manual_override'
                            ? 'Manual override'
                            : 'Price'}
                  </span>
                  <span className={styles.priceValue}>
                    £{(activeLine.unit_price ?? 0).toFixed(2)}
                  </span>
                  {!readOnly ? (
                    <button
                      type="button"
                      className={styles.overrideToggle}
                      onClick={togglePriceOverride}
                    >
                      {activeLine.unit_price_basis === 'manual_override'
                        ? 'Reset price'
                        : 'Override'}
                    </button>
                  ) : null}
                </div>
                {activeLine.unit_price_basis === 'manual_override' && !readOnly ? (
                  <div className={styles.overrideInputs}>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className={styles.priceInput}
                      value={activeLine.unit_price ?? 0}
                      onChange={(e) => handleManualPrice(parseFloat(e.target.value) || 0)}
                      onBlur={() => patchTicket({ lines: buildLineInputs(draftLines) })}
                      placeholder="0.00"
                    />
                    <input
                      type="text"
                      className={styles.reasonInput}
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      onBlur={handleOverrideReasonBlur}
                      placeholder="Reason for override (required)"
                    />
                  </div>
                ) : null}
              </div>

              {activeLine.unit_price != null && activeLine.unit_price > 0 ? (
                <div className={styles.lineTotalRow}>
                  <span className={styles.lineTotalLabel}>Line total</span>
                  <span className={styles.lineTotalValue}>
                    £{calculateLineTotal(activeLine).toFixed(2)}
                  </span>
                </div>
              ) : null}

              <div className={styles.lineRow}>
                {activeLine.mode === 'unit' ? (
                  <input
                    className={styles.qtyInput}
                    type="number"
                    min={0}
                    aria-label="Quantity"
                    value={activeLine.quantity}
                    disabled={readOnly}
                    onChange={(e) =>
                      updateLine(activeLine.uid, { quantity: Number(e.target.value) })
                    }
                  />
                ) : (
                  <span className={styles.muted}>Quantity from weight</span>
                )}
                <input
                  className={styles.ewcInput}
                  aria-label="EWC code"
                  placeholder="EWC"
                  value={activeLine.ewc_code}
                  disabled={readOnly}
                  onChange={(e) => updateLine(activeLine.uid, { ewc_code: e.target.value })}
                />
                {/* Amendment E: unit-mode lines get a material photo slot
                    instead of gross/tare — no weight leg to capture. */}
                {activeLine.mode === 'unit' && !readOnly ? (
                  <button
                    type="button"
                    className={styles.captureBtn}
                    onClick={() => void captureLineWeight(activeLine.uid, 'material')}
                    disabled={captureBridgeBlocked || capturingByUid[activeLine.uid]?.material === true}
                    aria-busy={capturingByUid[activeLine.uid]?.material === true}
                    title={captureBridgeBlocked ? 'Select a bridge' : 'Capture a photo for this item'}
                    aria-label="Capture material photo"
                  >
                    {capturingByUid[activeLine.uid]?.material ? (
                      <Spinner size="sm" label="Capturing material photo" />
                    ) : (
                      '📷'
                    )}
                  </button>
                ) : null}
                {!readOnly ? (
                  <button
                    type="button"
                    className={styles.secondaryBtn}
                    onClick={() => pushLines(draftLines.filter((x) => x.uid !== activeLine.uid))}
                  >
                    Delete
                  </button>
                ) : null}
              </div>
              {activeLine.mode === 'unit' ? (
                <KioskLinePhoto
                  scopeKey={ticketEncId}
                  label="Material"
                  directUrl={lineSnapshots[activeLine.uid]?.material}
                  storageKey={
                    lineSnapshots[activeLine.uid]?.material
                      ? undefined
                      : (lineImageKeysByUid[activeLine.uid]?.material ?? undefined)
                  }
                  capturing={capturingByUid[activeLine.uid]?.material === true}
                />
              ) : null}
            </>
          ) : (
            <p className={styles.muted}>
              {draftLines.length === 0
                ? 'Add a line using the search above or the + button'
                : 'Select a line to view details'}
            </p>
          )}
        </div>
      </div>

      {/* RIGHT — totals + actions */}
      <div className={styles.wsRight}>
        <div className={styles.wsRightTop}>
          <h3 className={styles.wsColTitle}>Totals</h3>
          <div className={styles.reviewRow}>
            <span>Lines</span>
            <strong data-testid="kiosk-total-lines">{lineCount}</strong>
          </div>
          <div className={styles.reviewRow}>
            <span>Net weight</span>
            <strong>{netTotal.toLocaleString()} kg</strong>
          </div>
          <div className={styles.reviewRow}>
            <span>Subtotal</span>
            <strong>£{(ticket?.subtotal ?? 0).toFixed(2)}</strong>
          </div>
          <div className={styles.reviewRow}>
            <span>VAT</span>
            <strong>£{(ticket?.vat_total ?? 0).toFixed(2)}</strong>
          </div>
          <div className={`${styles.reviewRow} ${styles.reviewTotal}`}>
            <span>Total</span>
            <strong data-testid="kiosk-total">£{(ticket?.total ?? 0).toFixed(2)}</strong>
          </div>
        </div>

        <div className={styles.wsRightBottom}>
          {error ? <div className={styles.error}>{error}</div> : null}
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => void onSaveDraft()}
            disabled={!canSaveDraft || busy}
            data-testid="kiosk-save-draft"
          >
            {status === 'open' ? 'Save as Draft' : 'Save Draft'}
          </button>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => void onMarkOpen()}
            disabled={!canMarkOpen || busy}
            data-testid="kiosk-mark-open"
          >
            {readOnly || status !== 'draft' ? 'Opened' : 'Mark as Open'}
          </button>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => void onPrint()}
            disabled={!canPrint || busy}
            data-testid="kiosk-print"
          >
            Print
          </button>
          {canDelete && (
            confirmingDelete ? (
              <div className={styles.voidConfirm}>
                <span className={styles.muted}>Delete this ticket? This cannot be undone.</span>
                <div className={styles.voidConfirmRow}>
                  <button
                    type="button"
                    className={styles.secondaryBtn}
                    onClick={() => setConfirmingDelete(false)}
                    disabled={busy}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={styles.dangerBtn}
                    onClick={async () => {
                      if (!ticket) return;
                      setBusy(true);
                      setError(null);
                      try {
                        await deleteWbTicket(ticket.enc_id);
                        onClose();
                      } catch {
                        setError('Could not delete the ticket.');
                        setConfirmingDelete(false);
                      } finally {
                        setBusy(false);
                      }
                    }}
                    disabled={busy}
                    data-testid="kiosk-delete-confirm"
                  >
                    Confirm Delete
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className={styles.outlineDangerBtn}
                onClick={() => setConfirmingDelete(true)}
                data-testid="kiosk-delete"
              >
                Delete ticket
              </button>
            )
          )}
        </div>
      </div>

      {ticket && (
        <NotesPanel
          open={notesOpen}
          onClose={() => setNotesOpen(false)}
          entityType="wb_ticket"
          encId={ticket.enc_id}
          entityLabel={`Ticket ${ticket.ticket_number ?? ticket.enc_id}`}
        />
      )}

      {vehicleRecognizedEvent && (
        <VehicleRecognitionPopup
          key={vehicleRecognizedEvent.registration}
          event={vehicleRecognizedEvent}
          onDismiss={dismissVehiclePopup}
          onUseVehicle={(v) => {
            onPickVehicle(v.enc_id);
            dismissVehiclePopup();
          }}
        />
      )}

      {addLineModalOpen ? (
        <div
          className={styles.modalOverlay}
          role="dialog"
          aria-modal="true"
          aria-label="Browse products"
          onClick={(e) => {
            if (e.target === e.currentTarget) setAddLineModalOpen(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setAddLineModalOpen(false);
          }}
        >
          <div className={styles.modalPanel}>
            <KioskProductBrowser
              products={products}
              groups={productGroups}
              onSelect={(encId) => addLineWithProduct(encId)}
              onClose={() => setAddLineModalOpen(false)}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
});
